import { computed, ref } from 'vue'
import type { IFilterPreset } from '@/modules/dashboard/types/IFilterPreset'

const STORAGE_KEY = 'materio.dashboard.filter-presets'

const loadStoredPresets = (): IFilterPreset[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    console.error('Failed to load filter presets from localStorage:', e)
    return []
  }
}

const saveStoredPresets = (presets: IFilterPreset[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets))
  } catch (e) {
    console.error('Failed to save filter presets to localStorage:', e)
  }
}

// Глобальное реактивное состояние пресетов
const allPresets = ref<IFilterPreset[]>(loadStoredPresets())

export function useFilterPresets() {
  const getPresetsForTarget = (target: string) => {
    return computed(() => allPresets.value.filter(p => p.target === target))
  }

  const addPreset = (
    target: string,
    label: string,
    filters: Record<string, any>,
    groupBy: string | null = null,
    dateTrunc: string | null = null,
  ): IFilterPreset => {
    // Глубокое клонирование активных фильтров для сохранения снимка
    const clonedFilters = JSON.parse(JSON.stringify(filters))

    const newPreset: IFilterPreset = {
      id: `preset_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      label: label.trim(),
      target,
      filters: clonedFilters,
      groupBy,
      dateTrunc,
      createdAt: Date.now(),
    }

    allPresets.value = [newPreset, ...allPresets.value]
    saveStoredPresets(allPresets.value)
    return newPreset
  }

  const renamePreset = (id: string, newLabel: string) => {
    const trimmed = newLabel.trim()
    if (!trimmed) return

    allPresets.value = allPresets.value.map(p => {
      if (p.id === id) {
        return { ...p, label: trimmed }
      }
      return p
    })
    saveStoredPresets(allPresets.value)
  }

  const deletePreset = (id: string) => {
    allPresets.value = allPresets.value.filter(p => p.id !== id)
    saveStoredPresets(allPresets.value)
  }

  /**
   * Проверяет, совпадает ли пресет с текущими активными фильтрами в сторе
   */
  const isPresetActive = (
    preset: IFilterPreset,
    currentFilters: Record<string, any>,
    currentGroupBy: string | null,
    currentDateTrunc: string | null,
  ): boolean => {
    // Сравнение группировки и среза дат
    if ((preset.groupBy || null) !== (currentGroupBy || null)) return false
    if ((preset.dateTrunc || null) !== (currentDateTrunc || null)) return false

    const presetKeys = Object.keys(preset.filters || {})
    const currentKeys = Object.keys(currentFilters || {})

    if (presetKeys.length !== currentKeys.length) return false

    for (const key of presetKeys) {
      if (!(key in currentFilters)) return false

      const presetVal = preset.filters[key]
      const currentVal = currentFilters[key]

      const extractIds = (val: any): string[] => {
        if (!val) return []
        if (Array.isArray(val)) {
          return val.map(item => String(typeof item === 'object' && item !== null ? item.id : item)).sort()
        }
        if (typeof val === 'object' && val !== null && 'id' in val) {
          return [String(val.id)]
        }
        return [String(val)]
      }

      const presetIds = extractIds(presetVal)
      const currentIds = extractIds(currentVal)

      if (presetIds.length !== currentIds.length) return false
      for (let i = 0; i < presetIds.length; i++) {
        if (presetIds[i] !== currentIds[i]) return false
      }
    }

    return true
  }

  return {
    allPresets,
    getPresetsForTarget,
    addPreset,
    renamePreset,
    deletePreset,
    isPresetActive,
  }
}
